/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Flex, Tooltip, Box, Icon, Button, Progress } from '@chakra-ui/react';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment-timezone';
import {
  addMinutes,
  combineTimeAndDate,
  formatEventToForm,
  formatInterviewerSchedule,
  formatBackgroundEvent,
  convertToScrollTime,
  constructStartAndEndTime,
} from '../utils';
import { useRetry } from '@/hooks/use-retry';
import { useAuth } from '@/hooks';
import {
  selectDuration,
  selectEventFetchId,
  selectInterviewDetails,
  selectCreatedInterviewDetails,
  selectTimezone,
  selectWeekRange,
} from '@/store/atsScheduling/selectors';
import {
  useFetchInterviewerEventMutation,
  useFetchInterviewersScheduleMutation,
} from '@/store/query/scheduling';
import { selectMeForUpdate } from '@/store/me';
import { editMe } from '@/store/me/actions';
import {
  updateDuration,
  updateEvents,
  updateTimezone,
  updateWeekRange,
} from '@/store/atsScheduling';
// import { getAvailableSlots } from './Algorithm/getAvailableSlots';
import { ChevronLeft } from '@kula-ai/icons/outline/ChevronLeft';
import { ChevronRight } from '@kula-ai/icons/outline/ChevronRight';
import { XClose } from '@kula-ai/icons/outline/XClose';
import { getCustomStyles } from '@/theme/components/select';
import { isSameTime } from '../utils';

import './index.css';
import { KText } from '@/ui-library/components/text';

function SchedulingCalendar({
  onEventChange,
  event,
  timeSlots,
  variant = 'manual',
  interviewerIds,
  jobId,
  setCbUserInfo,
  scheduledSlotsObj,
  changeDateRange = () => {},
  changedDateRange,
  interviewName: liveInterviewName,
}) {
  const dispatch = useDispatch();
  const [events, setEvents] = useState([]);
  const [interviewerEvents, setInterviewerEvents] = useState([]);
  const [timezoneSelectValue, setTimezoneSelectValue] = useState({});
  const calendarRef = useRef(null);
  const previousActiveDate = useRef(null);
  const { activeDate, activeStart, activeEnd, activeEndDate } = event;

  const interviewDetails = useSelector(selectInterviewDetails);
  const createdInterviewDetails = useSelector(selectCreatedInterviewDetails);
  const stateDuration = useSelector(selectDuration);
  const interviewerEventId = useSelector(selectEventFetchId);
  const selectedTimezone = useSelector(selectTimezone);
  const { time_zones } = useSelector(selectMeForUpdate);
  const weekRange = useSelector(selectWeekRange);
  const {
    currUser: { time_zone },
  } = useAuth();
  const [fetchInterviewerEvent] = useFetchInterviewerEventMutation();
  const [fetchInterviewersSchedule, { data: interviewerScheduleData }] =
    useFetchInterviewersScheduleMutation();
  const { name, duration } = interviewDetails || {};
  const { name: savedName } = createdInterviewDetails || {};
  const interviewName = name || savedName;
  const { scheduleSlots, isScheduleSlotLoading } = scheduledSlotsObj;

  useEffect(() => {
    dispatch(updateDuration(duration || 15));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  useEffect(() => {
    dispatch(editMe());
  }, [dispatch]);

  useEffect(() => {
    if (
      changedDateRange?.type === 'Custom' ||
      changedDateRange === 'Custom' ||
      changedDateRange === 'Selected Option'
    ) {
      const newEvents = events?.map((event) => ({
        ...event,
        end: addMinutes(event.start, stateDuration, selectedTimezone),
      }));

      setEvents(newEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateDuration]);

  useEffect(() => {
    const newTimeSlot = timeSlots?.map((slot, index) => {
      const id = uuidv4();
      if (index === 0) {
        jumpWeeksWithDate(slot.start_time, slot.start_time);
      }
      return {
        id,
        title: constructStartAndEndTime(slot.start_time, slot.end_time), // 'Candidate Option'
        start: slot.start_time,
        end: slot.end_time,
        classNames: 'auto-event-created',
        overlap: true,
      };
    });
    setEvents(newTimeSlot || []);
  }, [timeSlots]);

  useEffect(() => {
    if (scheduleSlots) {
      const newTimeSlot = Object.values(scheduleSlots)
        .flat()
        .map((slot, index) => {
          const id = uuidv4();
          if (index === 0) {
            jumpWeeksWithDate(slot.start_at, slot.start_at);
          }
          return {
            id,
            title: constructStartAndEndTime(slot.start_at, slot.end_at), //'Candidate Option',
            start: slot.start_at,
            end: slot.end_at,
            classNames: 'auto-event-created',
            overlap: true,
          };
        });
      setEvents(newTimeSlot || []);
    }
  }, [scheduleSlots]);

  useEffect(() => {
    dispatch(updateEvents(events));
  }, [dispatch, events]);

  useEffect(() => {
    if (variant === 'manual' && events?.length) {
      const newEvent = { ...events[0], title: liveInterviewName || 'Event' };
      setEvents([newEvent]);
    }
  }, [liveInterviewName]);

  const getEvents = () => {
    return new Promise(async (resolve, reject) => {
      try {
        if (interviewerEventId) {
          const res = await fetchInterviewerEvent({
            params: {
              fetch_uuid: interviewerEventId?.fetch_uuid,
              ats_job_id: jobId,
            },
          });
          if (res?.data?.status !== 'SUCCESS') {
            reject(res?.errors);
            return;
          }
          resolve(res?.data?.results);
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  const {
    data: eventData,
    inProgress,
    // error,
  } = useRetry({
    fn: getEvents,
    args: {},
    retryOptions: { factor: 1, retries: 20, minTimeout: 1000 },
    start: interviewerEventId,
  });

  useEffect(() => {
    const { users_info: userInfo } = eventData || {};
    const eventsResponse = formatInterviewerSchedule(eventData);
    setInterviewerEvents(eventsResponse);
    setCbUserInfo(userInfo);
    //Auto fill slots
    // const availableEvents = getAvailableSlots(
    //   eventsResponse,
    //   30,
    //   '09:00',
    //   '18:00'
    // );
    // setEvents(availableEvents);
  }, [eventData]);

  useEffect(() => {
    if (changedDateRange === 'Custom') {
      resetSlots();
    }
  }, [changedDateRange]);

  const resetSlots = () => {
    setEvents([]);
  };

  const jumpWeeksWithDate = (targetDate, activeStart = '') => {
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(targetDate);
      if (activeStart) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.scrollToTime(convertToScrollTime(activeStart));
      }
    }
  };

  useEffect(() => {
    if (interviewerIds?.length) {
      fetchInterviewersSchedule({
        weekStart: weekRange.start,
        timezone: selectedTimezone,
        interviewerIds,
      });
    } else {
      setInterviewerEvents([]);
    }
  }, [weekRange, selectedTimezone, interviewerIds]);

  useEffect(() => {
    setInterviewerEvents([]);

    return () => {
      setInterviewerEvents([]);
    };
  }, []);

  useEffect(() => {
    const { noJump } = event;
    if (activeDate) {
      if (
        previousActiveDate.current &&
        activeDate.getTime() !== previousActiveDate.current.getTime()
      ) {
        if (!noJump) jumpWeeksWithDate(activeDate);
      }
      previousActiveDate.current = activeDate;
    }
  }, [activeDate]);

  //Association with interview form date, start and end time.
  useEffect(() => {
    const { noJump } = event;
    if (activeDate && activeStart && activeEnd && activeEndDate) {
      if (!noJump) jumpWeeksWithDate(activeDate, activeStart);
      const id = uuidv4();
      const newEvent = {
        id,
        title: liveInterviewName || interviewName || 'Event',
        start: combineTimeAndDate(activeStart, activeDate, selectedTimezone),
        end: combineTimeAndDate(activeEnd, activeEndDate, selectedTimezone),
        allDay: false,
        classNames: `event-created`,
        overlap: true,
      };
      setEvents([newEvent]);
    }
  }, [activeDate, activeEnd, activeStart, activeEndDate, event]);

  const removeEvent = (eventInfo) => {
    const targetId = eventInfo.event.id;
    const newEvents = events.filter((event) => event.id !== targetId);
    setEvents(newEvents);
    changeDateRange({ type: 'Custom' });
  };

  const renderEventContent = (eventInfo) => {
    const { name, type } = eventInfo.event.extendedProps || {};
    const eventDuration =
      moment(eventInfo.event.endStr).diff(moment(eventInfo.event.startStr)) /
      1000 /
      60;

    const timeStr =
      moment.tz(eventInfo.event.startStr, selectedTimezone).format('h.mm A') +
      ' - ' +
      moment.tz(eventInfo.event.endStr, selectedTimezone).format('h.mm A');

    if (type === 'interviewer-event') {
      return (
        <Tooltip
          label={`${eventInfo.event.title} (${moment
            .tz(eventInfo.event.startStr, selectedTimezone)
            .format('h.mm A')}) - ${name}`}
          hasArrow
          placement="top"
          background="#161616"
          borderRadius="3px"
          arrowSize={8}
          marginBottom="4px"
          fontSize="12px"
        >
          <Box p="0px 2px">
            {eventDuration > 30 ? (
              <>
                <KText
                  className="text"
                  fontSize="12px"
                  fontWeight="semiBold"
                  isTruncated
                  enableTooltip={false}
                >
                  {eventInfo.event.title}
                </KText>
                <KText
                  enableTooltip={false}
                  className="text"
                  fontSize="12px"
                  isTruncated
                >
                  {moment
                    .tz(eventInfo.event.startStr, selectedTimezone)
                    .format('h.mm A')}{' '}
                  -{' '}
                  {moment
                    .tz(eventInfo.event.endStr, selectedTimezone)
                    .format('h.mm A')}
                </KText>
              </>
            ) : (
              <KText
                display="flex"
                gap="2px"
                alignItems="center"
                className="text"
                fontSize="12px"
                fontWeight="semiBold"
                isTruncated
                enableTooltip={false}
              >
                {eventInfo.event.title}{' '}
                <KText className="text" fontSize="12px" fontWeight="400">
                  {moment
                    .tz(eventInfo.event.startStr, selectedTimezone)
                    .format('h.mm A')}
                </KText>
              </KText>
            )}

            {eventDuration > 45 && (
              <KText
                enableTooltip={false}
                className="text"
                fontSize="12px"
                isTruncated
              >
                {name}
              </KText>
            )}
          </Box>
        </Tooltip>
      );
    } else if (type === 'un-available-slots') {
      return (
        <Box position="relative">
          <KText
            className="text"
            fontSize="12px"
            fontWeight="medium"
            isTruncated
            enableTooltip={false}
          >
            {eventInfo.event.title}
          </KText>
        </Box>
      );
    } else {
      if (!eventInfo.event.title) return;
      return (
        <Tooltip
          label={variant === 'manual' ? eventInfo.event.title : timeStr}
          hasArrow
          placement="top"
          background="#161616"
          borderRadius="3px"
          arrowSize={8}
          marginBottom="4px"
          fontSize="12px"
        >
          <Box position="relative" h="100%" p={eventDuration > 30 && '2px'}>
            {variant !== 'manual' && (
              <Flex
                position="absolute"
                right="-12px"
                top="-10px"
                border="1px solid #605AFB"
                bg="#FFF"
                borderRadius="50%"
                alignItems="center"
                justifyContent="center"
                onClick={() => removeEvent(eventInfo)}
                zIndex="999"
                p="0"
                m="0"
              >
                <Flex
                  bg="#605AFB"
                  w="14px"
                  h="14px"
                  borderRadius="50%"
                  alignItems="center"
                  justifyContent="center"
                  m="1px"
                  boxSize="border-box"
                >
                  <Icon as={XClose} w="12px" h="12px" zIndex="999" />
                </Flex>
              </Flex>
            )}
            <Flex flexDirection="column" height="100%">
              <KText
                className="text"
                fontSize={eventDuration <= 45 ? '10px' : '12px'}
                lineHeight={eventDuration <= 45 && '10px'}
                fontWeight="medium"
                isTruncated
                enableTooltip={false}
              >
                {variant === 'manual' ? eventInfo.event.title : timeStr}
              </KText>
              {variant === 'manual' && eventDuration > 15 && (
                <KText
                  className="text"
                  fontSize={eventDuration <= 45 ? '10px' : '12px'}
                  mt={eventDuration <= 45 && '2px'}
                  fontWeight="medium"
                  isTruncated
                  enableTooltip={false}
                >
                  {timeStr}
                </KText>
              )}
            </Flex>
          </Box>
        </Tooltip>
      );
    }
  };

  const handleEventSelect = (info) => {
    const id = uuidv4();
    const duration = (info.end - info.start) / (1000 * 60); // Duration in minutes
    const endAt =
      variant === 'manual'
        ? duration === 15
          ? addMinutes(info.startStr, stateDuration || 15, selectedTimezone)
          : info.endStr
        : addMinutes(info.startStr, stateDuration || 15, selectedTimezone);
    const newEvent = {
      id,
      title:
        variant === 'manual'
          ? interviewName || 'Event'
          : constructStartAndEndTime(info.startStr, endAt), //Candidate Option
      start: info.startStr,
      end: endAt,
      allDay: info.allDay,
      classNames: variant === 'manual' ? `event-created` : 'auto-event-created',
      overlap: true,
    };

    if (variant === 'manual') {
      setEvents([newEvent]);
      onEventChange(formatEventToForm(newEvent));
    } else {
      const uniqueEvents = events.filter(
        (event) =>
          !(event.start === newEvent.start && event.end === newEvent.end)
      );
      changeDateRange({ type: 'Custom' });
      setEvents([...uniqueEvents, newEvent]);
    }
  };

  const handleEventDropOrResize = (info) => {
    const updatedEvents = events
      .map((event) => {
        if (event?.id === info?.event?.id) {
          const duplicateEvent = events.find((event) => {
            return (
              event.id !== info.event.id &&
              isSameTime(event.start, info.event.startStr) &&
              isSameTime(
                event.end,
                addMinutes(info.event.startStr, stateDuration, selectedTimezone)
              )
            );
          });

          if (duplicateEvent) return null;
          return {
            ...event,
            start: info.event.startStr,
            end:
              variant === 'manual'
                ? info.event.endStr
                : addMinutes(
                    info.event.startStr,
                    stateDuration,
                    selectedTimezone
                  ),
          };
        }
        return event;
      })
      .filter((event) => event);
    if (variant === 'manual') {
      setEvents(updatedEvents);
      onEventChange(formatEventToForm(updatedEvents[0]));
    } else {
      changeDateRange({ type: 'Custom' });
      setEvents(updatedEvents);
    }
  };

  const optionsForTimeZone = useMemo(
    () =>
      Object.entries(time_zones || {}).map(([key, val]) => ({
        label: `(GMT${val}) ${key}`,
        value: key,
      })),
    [time_zones]
  );

  useEffect(() => {
    dispatch(updateTimezone(time_zone));
  }, []);

  useEffect(() => {
    const timeZoneObj = optionsForTimeZone.find(
      (timezone) => timezone.value === selectedTimezone
    );
    setTimezoneSelectValue(timeZoneObj);
  }, [selectedTimezone, optionsForTimeZone]);

  const handleEventChange = (timezone) => {
    if (events.length) {
      const newEvent = {
        ...events[0],
        start: moment
          .tz(events[0]?.start, timezone)
          .format('YYYY-MM-DDTHH:mm:ssZ'),
        end: moment.tz(events[0]?.end, timezone).format('YYYY-MM-DDTHH:mm:ssZ'),
      };
      setEvents([newEvent]);
      onEventChange(formatEventToForm(newEvent));
    }
  };

  const renderHeader = () => {
    const calendarApi = calendarRef?.current?.getApi();

    const currentMonth = calendarRef?.current?.calendar?.currentData?.viewTitle;

    const onChangeTimeZone = (event) => {
      dispatch(updateTimezone(event.value));
      if (variant === 'manual') {
        handleEventChange(event.value);
      }
    };

    return (
      <Flex
        justifyContent="space-between"
        minH="60px"
        alignItems="center"
        p="0px 10px"
      >
        <Flex gap="6px" alignItems="stretch">
          <Flex
            border="1px solid #EFECF0"
            alignItems="center"
            borderRadius="8px"
          >
            <Button
              variant="ghost"
              borderRadius="none"
              px="5px"
              onClick={() => calendarApi.prev()}
            >
              <Icon as={ChevronLeft} w="15px" h="15px" />
            </Button>
            <Button
              variant="ghost"
              borderRadius="none"
              px="5px"
              onClick={() => calendarApi.next()}
            >
              <Icon as={ChevronRight} w="15px" h="15px" />
            </Button>
          </Flex>
          <Button
            variant="quaternary-lg"
            borderRadius="md"
            onClick={() => calendarApi.today()}
            py="5px"
            h="40px"
          >
            Current week
          </Button>
        </Flex>
        <KText size="sm-semi-bold" color="secondary-900">
          {currentMonth}
        </KText>
        <Flex gap="6px" alignItems="center">
          {variant !== 'manual' && !!events.length && (
            <Button
              variant="quaternary-lg"
              borderRadius="8px"
              onClick={() => {
                setEvents([]);
                changeDateRange({ type: 'Custom' });
              }}
              py="5px"
              h="40px"
            >
              Clear selection
            </Button>
          )}
          <Select
            id="timezone"
            value={timezoneSelectValue}
            options={optionsForTimeZone}
            styles={getCustomStyles([
              {
                type: 'control',
                newStyles: {
                  width: '300px',
                  height: '44px',
                  minHeight: '38px',
                  overflow: 'visible',
                },
              },
              {
                type: 'valueContainer',
                newStyles: {
                  minHeight: '38px',
                },
              },
            ])}
            placeholder="Please select..."
            onChange={onChangeTimeZone}
          />
        </Flex>
      </Flex>
    );
  };

  const calendarConfig = useMemo(
    () => ({
      plugins: [interactionPlugin, timeGridPlugin, momentTimezonePlugin],
      initialView: 'timeGridWeek',
      nowIndicator: true,
      headerToolbar: false,
      titleFormat: { year: 'numeric', month: 'long' },
      datesSet: function (dateInfo) {
        dispatch(
          updateWeekRange({
            start: dateInfo?.startStr,
            end: dateInfo?.endStr,
          })
        );
        const titles = document.querySelectorAll('.fc-scrollgrid-sync-inner');
        titles.forEach((dayHeaderEl) => {
          const link = dayHeaderEl.querySelector('a');
          var text = link?.textContent;
          dayHeaderEl.innerHTML = `<div class="wrapper-cal-header"><span class="day">${
            text?.split(' ')[0]
          }</span><span class="date">${
            text?.split(' ')[1]?.split('/')[1]
          }</span></div>`;
        });
      },
      initialDate: activeDate ? new Date(activeDate) : new Date(),
      editable: true,
      eventContent: renderEventContent,
      allDaySlot: false,
      slotDuration: '00:15:00',
      slotLabelInterval: '01:00:00',
      scrollTime: '09:00:00',
      height: '100%',
      eventOverlap: false,
      timeZone: selectedTimezone,
      events: [
        ...interviewerEvents,
        ...events,
        ...formatBackgroundEvent(interviewerScheduleData),
        ...[
          {
            start: moment()
              ?.tz(selectedTimezone)
              ?.subtract(10, 'year')
              ?.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: moment()?.tz(selectedTimezone)?.format('YYYY-MM-DDTHH:mm:ssZ'),
            display: 'background',
            classNames: 'bg-disabled-slots',
            color: 'transparent',
          },
        ],
      ],
      eventDrop: handleEventDropOrResize,
      eventDurationEditable: variant === 'manual',
      eventResize: handleEventDropOrResize,
      eventResizableFromStart: variant === 'manual',
      selectable: true,
      select: handleEventSelect,
      selectAllow: function (selectInfo) {
        return moment().diff(selectInfo.start) <= 0;
      },
      selectConstraint: {
        start: '00:00', // Restrict selection to start at midnight
        end: '23:59', // Restrict selection to end just before midnight
      },
      slotLabelFormat: {
        hour: 'numeric',
        meridiem: true,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeDate, interviewerEvents, events, selectedTimezone]
  );

  return (
    <Flex
      flexDirection="column"
      height="100%"
      className="scheduling-cal-wrapper"
    >
      {renderHeader()}
      <Box flex={1}>
        {(inProgress || isScheduleSlotLoading) && (
          <Progress size="xs" isIndeterminate colorScheme="purple" />
        )}
        <FullCalendar
          key={selectedTimezone}
          ref={calendarRef}
          {...calendarConfig}
        />
        ;
      </Box>
    </Flex>
  );
}

export default SchedulingCalendar;
